/**
 * CoachPlaybook - Baseball Sport Module
 * Version: 0.6.0
 * Description: Complete baseball configuration — positions, actions, formations, age groups
 */

const baseball = {
  id: 'baseball',
  name: 'Baseball',
  icon: '⚾',
  accentColor: '#dc2626',
  surfaceType: 'diamond',

  // Field views available
  views: ['full-field', 'infield', 'outfield', 'diamond'],
  defaultView: 'full-field',
  orientation: ['vertical', 'horizontal'],

  // Default player counts
  playersPerSide: 9,
  maxDefenders: 9,

  // Age group configurations
  ageGroups: [
    { id: 'tball', label: 'T-Ball (U6)', players: 9, basePaths: 50, moundDist: null, outfieldFence: 150, innings: 3, pitchType: 'tee' },
    { id: 'coach-pitch', label: 'Coach Pitch (U8)', players: 9, basePaths: 60, moundDist: 38, outfieldFence: 175, innings: 6, pitchType: 'coach' },
    { id: 'kid-pitch', label: 'Kid Pitch (U10)', players: 9, basePaths: 60, moundDist: 46, outfieldFence: 200, innings: 6, pitchType: 'player' },
    { id: 'little-league', label: 'Little League (U12)', players: 9, basePaths: 60, moundDist: 46, outfieldFence: 200, innings: 6, pitchType: 'player' },
    { id: 'youth', label: 'Youth (U14)', players: 9, basePaths: 70, moundDist: 50, outfieldFence: 275, innings: 7, pitchType: 'player' },
    { id: 'high', label: 'High School', players: 9, basePaths: 90, moundDist: 60.5, outfieldFence: 330, innings: 7, pitchType: 'player' },
    { id: 'college', label: 'College', players: 9, basePaths: 90, moundDist: 60.5, outfieldFence: 380, innings: 9, pitchType: 'player' },
    { id: 'pro', label: 'Pro/MLB', players: 9, basePaths: 90, moundDist: 60.5, outfieldFence: 400, innings: 9, pitchType: 'player' }
  ],

  // Positions
  positions: [
    { id: 'P', name: 'Pitcher', short: 'P', number: 1, defaultColor: '#dc2626', role: 'defense' },
    { id: 'C', name: 'Catcher', short: 'C', number: 2, defaultColor: '#7c3aed', role: 'defense' },
    { id: '1B', name: 'First Baseman', short: '1B', number: 3, defaultColor: '#2563eb', role: 'defense' },
    { id: '2B', name: 'Second Baseman', short: '2B', number: 4, defaultColor: '#0891b2', role: 'defense' },
    { id: '3B', name: 'Third Baseman', short: '3B', number: 5, defaultColor: '#059669', role: 'defense' },
    { id: 'SS', name: 'Shortstop', short: 'SS', number: 6, defaultColor: '#d97706', role: 'defense' },
    { id: 'LF', name: 'Left Fielder', short: 'LF', number: 7, defaultColor: '#e11d48', role: 'defense' },
    { id: 'CF', name: 'Center Fielder', short: 'CF', number: 8, defaultColor: '#4f46e5', role: 'defense' },
    { id: 'RF', name: 'Right Fielder', short: 'RF', number: 9, defaultColor: '#ca8a04', role: 'defense' },
    { id: 'DH', name: 'Designated Hitter', short: 'DH', number: 0, defaultColor: '#6b7280', role: 'offense' }
  ],

  // Default standard alignment (percentage-based coordinates, home plate at bottom center)
  defaultFormation: {
    name: 'Standard Alignment',
    positions: {
      P:  { x: 50, y: 60, hasBall: true },
      C:  { x: 50, y: 90, hasBall: false },
      '1B': { x: 72, y: 58, hasBall: false },
      '2B': { x: 60, y: 48, hasBall: false },
      '3B': { x: 28, y: 58, hasBall: false },
      SS: { x: 40, y: 48, hasBall: false },
      LF: { x: 22, y: 25, hasBall: false },
      CF: { x: 50, y: 18, hasBall: false },
      RF: { x: 78, y: 25, hasBall: false }
    }
  },

  // Common formations / defensive alignments
  formations: [
    {
      id: 'standard',
      name: 'Standard Alignment',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 72, y: 58 }, '2B': { x: 60, y: 48 }, '3B': { x: 28, y: 58 }, SS: { x: 40, y: 48 }, LF: { x: 22, y: 25 }, CF: { x: 50, y: 18 }, RF: { x: 78, y: 25 } }
    },
    {
      id: 'shift-left',
      name: 'Shift Left (Pull Hitter)',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 72, y: 58 }, '2B': { x: 50, y: 48 }, '3B': { x: 28, y: 58 }, SS: { x: 35, y: 42 }, LF: { x: 18, y: 22 }, CF: { x: 38, y: 18 }, RF: { x: 62, y: 22 } }
    },
    {
      id: 'shift-right',
      name: 'Shift Right (Opposite Field)',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 72, y: 58 }, '2B': { x: 65, y: 42 }, '3B': { x: 28, y: 58 }, SS: { x: 50, y: 48 }, LF: { x: 38, y: 22 }, CF: { x: 62, y: 18 }, RF: { x: 82, y: 22 } }
    },
    {
      id: 'no-doubles',
      name: 'No Doubles',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 76, y: 55 }, '2B': { x: 62, y: 45 }, '3B': { x: 24, y: 55 }, SS: { x: 38, y: 45 }, LF: { x: 15, y: 18 }, CF: { x: 50, y: 12 }, RF: { x: 85, y: 18 } }
    },
    {
      id: 'infield-in',
      name: 'Infield In',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 68, y: 65 }, '2B': { x: 58, y: 58 }, '3B': { x: 32, y: 65 }, SS: { x: 42, y: 58 }, LF: { x: 22, y: 25 }, CF: { x: 50, y: 18 }, RF: { x: 78, y: 25 } }
    },
    {
      id: 'outfield-in',
      name: 'Outfield In (Shallow)',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 72, y: 58 }, '2B': { x: 60, y: 48 }, '3B': { x: 28, y: 58 }, SS: { x: 40, y: 48 }, LF: { x: 25, y: 35 }, CF: { x: 50, y: 30 }, RF: { x: 75, y: 35 } }
    },
    {
      id: 'bunt-defense',
      name: 'Bunt Defense',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 65, y: 68 }, '2B': { x: 62, y: 48 }, '3B': { x: 35, y: 68 }, SS: { x: 40, y: 48 }, LF: { x: 22, y: 25 }, CF: { x: 50, y: 18 }, RF: { x: 78, y: 25 } }
    },
    {
      id: 'double-play-depth',
      name: 'Double Play Depth',
      type: 'defense',
      positions: { P: { x: 50, y: 60 }, C: { x: 50, y: 90 }, '1B': { x: 70, y: 60 }, '2B': { x: 58, y: 50 }, '3B': { x: 30, y: 60 }, SS: { x: 42, y: 50 }, LF: { x: 22, y: 25 }, CF: { x: 50, y: 18 }, RF: { x: 78, y: 25 } }
    }
  ],

  // Complete action library
  actions: {
    offense: [
      // Hitting
      { id: 'hit', name: 'Hit', icon: '🏏', lineStyle: 'solid', category: 'hitting' },
      { id: 'bunt', name: 'Bunt', icon: '👆', lineStyle: 'solid', category: 'hitting' },
      { id: 'sac-bunt', name: 'Sacrifice Bunt', icon: '🎯', lineStyle: 'solid', category: 'hitting' },
      { id: 'squeeze', name: 'Squeeze Play', icon: '💥', lineStyle: 'solid', category: 'hitting' },
      { id: 'hit-and-run', name: 'Hit and Run', icon: '🏃', lineStyle: 'solid', category: 'hitting' },
      { id: 'hit-behind-runner', name: 'Hit Behind Runner', icon: '↗️', lineStyle: 'solid', category: 'hitting' },
      { id: 'opposite-field', name: 'Opposite Field', icon: '↘️', lineStyle: 'arrow', category: 'hitting' },
      { id: 'pull', name: 'Pull Hit', icon: '↙️', lineStyle: 'arrow', category: 'hitting' },
      { id: 'sac-fly', name: 'Sacrifice Fly', icon: '🛫', lineStyle: 'arrow-lob', category: 'hitting' },
      { id: 'line-drive', name: 'Line Drive', icon: '➡️', lineStyle: 'solid', category: 'hitting' },
      { id: 'ground-ball', name: 'Ground Ball', icon: '⬇️', lineStyle: 'solid', category: 'hitting' },
      { id: 'fly-ball', name: 'Fly Ball', icon: '⬆️', lineStyle: 'arrow-lob', category: 'hitting' },
      { id: 'drag-bunt', name: 'Drag Bunt', icon: '🏃‍♂️', lineStyle: 'solid', category: 'hitting' },
      { id: 'push-bunt', name: 'Push Bunt', icon: '👉', lineStyle: 'solid', category: 'hitting' },
      { id: 'slash', name: 'Slash (Fake Bunt Swing)', icon: '⚔️', lineStyle: 'solid', category: 'hitting' },
      // Baserunning
      { id: 'steal', name: 'Steal', icon: '💨', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'delayed-steal', name: 'Delayed Steal', icon: '⏳', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'lead-off', name: 'Lead Off', icon: '🦶', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'primary-lead', name: 'Primary Lead', icon: '1️⃣', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'secondary-lead', name: 'Secondary Lead', icon: '2️⃣', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'return-to-base', name: 'Return to Base', icon: '↩️', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'tag-up', name: 'Tag Up', icon: '🏷️', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'advance', name: 'Advance', icon: '⏩', lineStyle: 'solid', category: 'baserunning' },
      { id: 'slide', name: 'Slide', icon: '🛝', lineStyle: 'solid', category: 'baserunning' },
      { id: 'headfirst-slide', name: 'Headfirst Slide', icon: '🤿', lineStyle: 'solid', category: 'baserunning' },
      { id: 'round-base', name: 'Round Base', icon: '🔄', lineStyle: 'curved', category: 'baserunning' },
      { id: 'touch-and-go', name: 'Touch and Go', icon: '👋', lineStyle: 'solid', category: 'baserunning' },
      { id: 'running-on-contact', name: 'Running on Contact', icon: '🏃‍♂️', lineStyle: 'solid', category: 'baserunning' },
      { id: 'hit-and-run-read', name: 'Hit-and-Run Read', icon: '👁️', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'freeze-line-drive', name: 'Freeze on Line Drive', icon: '🧊', lineStyle: 'none', category: 'baserunning' },
      { id: 'steal-attempt', name: 'Steal Attempt', icon: '🚀', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'read-wild-pitch', name: 'Read Wild Pitch/Passed Ball', icon: '❗', lineStyle: 'dashed', category: 'baserunning' },
      { id: 'score', name: 'Score', icon: '🏠', lineStyle: 'solid', category: 'baserunning' }
    ],
    defense: [
      // Pitching
      { id: 'pitch', name: 'Pitch', icon: '⚾', lineStyle: 'arrow', category: 'pitching' },
      { id: 'intentional-walk', name: 'Intentional Walk', icon: '🚶', lineStyle: 'none', category: 'pitching' },
      { id: 'pickoff', name: 'Pickoff Attempt', icon: '🎯', lineStyle: 'arrow', category: 'pitching' },
      { id: 'pitch-out', name: 'Pitch Out', icon: '↗️', lineStyle: 'arrow', category: 'pitching' },
      { id: 'step-off', name: 'Step Off Rubber', icon: '🦶', lineStyle: 'none', category: 'pitching' },
      // Throwing
      { id: 'throw', name: 'Throw', icon: '💪', lineStyle: 'arrow', category: 'throwing' },
      { id: 'relay', name: 'Relay / Cutoff', icon: '🔗', lineStyle: 'arrow', category: 'throwing' },
      { id: 'cutoff-throw', name: 'Cutoff Throw', icon: '✂️', lineStyle: 'arrow', category: 'throwing' },
      { id: 'flip', name: 'Flip / Underhand Toss', icon: '🤏', lineStyle: 'arrow-short', category: 'throwing' },
      // Fielding
      { id: 'field-grounder', name: 'Field Ground Ball', icon: '⬇️', lineStyle: 'solid', category: 'fielding' },
      { id: 'field-fly', name: 'Field Fly Ball', icon: '⬆️', lineStyle: 'solid', category: 'fielding' },
      { id: 'charge-bunt', name: 'Charge Bunt', icon: '🏃', lineStyle: 'solid', category: 'fielding' },
      { id: 'dive', name: 'Diving Play', icon: '🤸', lineStyle: 'solid', category: 'fielding' },
      { id: 'backhand', name: 'Backhand', icon: '🖐️', lineStyle: 'solid', category: 'fielding' },
      { id: 'scoop', name: 'Short Hop / Scoop', icon: '🥄', lineStyle: 'solid', category: 'fielding' },
      { id: 'pop-fly-comm', name: 'Pop Fly Communication', icon: '📢', lineStyle: 'none', category: 'fielding' },
      // Tags and outs
      { id: 'tag', name: 'Tag', icon: '🏷️', lineStyle: 'none', category: 'outs' },
      { id: 'force-out', name: 'Force Out', icon: '🛑', lineStyle: 'none', category: 'outs' },
      { id: 'double-play-turn', name: 'Double Play Turn', icon: '2️⃣', lineStyle: 'arrow', category: 'outs' },
      { id: 'triple-play', name: 'Triple Play', icon: '3️⃣', lineStyle: 'arrow', category: 'outs' },
      { id: 'rundown', name: 'Rundown', icon: '🔄', lineStyle: 'zigzag', category: 'outs' },
      { id: 'appeal-play', name: 'Appeal Play', icon: '☝️', lineStyle: 'none', category: 'outs' },
      // Positioning
      { id: 'back-up', name: 'Back Up', icon: '🔙', lineStyle: 'dashed', category: 'positioning' },
      { id: 'shift', name: 'Shift', icon: '↔️', lineStyle: 'dashed', category: 'positioning' },
      { id: 'cover-base', name: 'Cover Base', icon: '🛡️', lineStyle: 'dashed', category: 'positioning' },
      { id: 'move-to-cutoff', name: 'Move to Cutoff Position', icon: '📍', lineStyle: 'dashed', category: 'positioning' },
      { id: 'shallow', name: 'Play Shallow', icon: '⬆️', lineStyle: 'dashed', category: 'positioning' },
      { id: 'deep', name: 'Play Deep', icon: '⬇️', lineStyle: 'dashed', category: 'positioning' },
      { id: 'hold-runner', name: 'Hold Runner', icon: '✋', lineStyle: 'none', category: 'positioning' },
      { id: 'cover-steal', name: 'Cover Steal', icon: '🏃‍♂️', lineStyle: 'dashed', category: 'positioning' }
    ],
    baserunning: [
      { id: 'br-primary-lead', name: 'Primary Lead', icon: '1️⃣', lineStyle: 'dashed', category: 'leads' },
      { id: 'br-secondary-lead', name: 'Secondary Lead', icon: '2️⃣', lineStyle: 'dashed', category: 'leads' },
      { id: 'br-return', name: 'Return to Base', icon: '↩️', lineStyle: 'dashed', category: 'leads' },
      { id: 'br-steal', name: 'Steal Attempt', icon: '💨', lineStyle: 'solid', category: 'steal' },
      { id: 'br-delayed-steal', name: 'Delayed Steal', icon: '⏳', lineStyle: 'dashed', category: 'steal' },
      { id: 'br-hit-and-run', name: 'Hit-and-Run Read', icon: '👁️', lineStyle: 'dashed', category: 'situational' },
      { id: 'br-freeze', name: 'Freeze on Line Drive', icon: '🧊', lineStyle: 'none', category: 'situational' },
      { id: 'br-tag-up', name: 'Tag Up', icon: '🏷️', lineStyle: 'dashed', category: 'situational' },
      { id: 'br-contact', name: 'Running on Contact', icon: '🏃', lineStyle: 'solid', category: 'situational' },
      { id: 'br-wild-pitch', name: 'Read Wild Pitch / Passed Ball', icon: '❗', lineStyle: 'dashed', category: 'situational' },
      { id: 'br-advance', name: 'Advance on Throw', icon: '⏩', lineStyle: 'solid', category: 'situational' },
      { id: 'br-score', name: 'Score', icon: '🏠', lineStyle: 'solid', category: 'situational' }
    ],
    cutoffRelay: [
      { id: 'cut-single-lf', name: 'Single to LF (Cutoff)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-single-cf', name: 'Single to CF (Cutoff)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-single-rf', name: 'Single to RF (Cutoff)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-extra-lf', name: 'Extra Base to LF (Relay)', icon: '🔗', lineStyle: 'arrow', category: 'relay' },
      { id: 'cut-extra-cf', name: 'Extra Base to CF (Relay)', icon: '🔗', lineStyle: 'arrow', category: 'relay' },
      { id: 'cut-extra-rf', name: 'Extra Base to RF (Relay)', icon: '🔗', lineStyle: 'arrow', category: 'relay' },
      { id: 'cut-fly-lf', name: 'Fly Ball to LF (Tag Up)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-fly-cf', name: 'Fly Ball to CF (Tag Up)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-fly-rf', name: 'Fly Ball to RF (Tag Up)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' },
      { id: 'cut-bunt', name: 'Bunt Situation (Cutoff)', icon: '🔗', lineStyle: 'arrow', category: 'cutoff' }
    ]
  },

  // Play categories
  playTypes: {
    offense: [
      { id: 'hit-and-run', label: 'Hit and Run' },
      { id: 'steal-play', label: 'Steal Play' },
      { id: 'double-steal', label: 'Double Steal' },
      { id: 'delayed-steal-play', label: 'Delayed Steal' },
      { id: 'sacrifice-bunt', label: 'Sacrifice Bunt' },
      { id: 'squeeze-play', label: 'Squeeze Play' },
      { id: 'safety-squeeze', label: 'Safety Squeeze' },
      { id: 'suicide-squeeze', label: 'Suicide Squeeze' },
      { id: 'slash-play', label: 'Slash (Fake Bunt Swing)' },
      { id: 'run-and-hit', label: 'Run and Hit' },
      { id: 'bunt-and-run', label: 'Bunt and Run' },
      { id: 'drag-bunt-play', label: 'Drag Bunt for Hit' },
      { id: 'hit-behind-runner-play', label: 'Hit Behind Runner' }
    ],
    defense: [
      { id: 'bunt-coverage', label: 'Bunt Coverage' },
      { id: 'cutoff-alignment', label: 'Cutoff / Relay Alignment' },
      { id: 'first-and-third-defense', label: 'First and Third Defense' },
      { id: 'pickoff-play', label: 'Pickoff Play' },
      { id: 'pickoff-daylight', label: 'Pickoff (Daylight Play)' },
      { id: 'pickoff-timing', label: 'Pickoff (Timing Play)' },
      { id: 'shift-configuration', label: 'Defensive Shift' },
      { id: 'double-play-feed', label: 'Double Play Feed' },
      { id: '6-4-3', label: '6-4-3 Double Play' },
      { id: '4-6-3', label: '4-6-3 Double Play' },
      { id: '5-4-3', label: '5-4-3 Double Play' },
      { id: '3-6-3', label: '3-6-3 Double Play' },
      { id: '1-6-3', label: '1-6-3 Double Play' },
      { id: 'rundown-defense', label: 'Rundown Play' },
      { id: 'wheel-play', label: 'Wheel Play (Bunt)' },
      { id: 'rotation-play', label: 'Rotation Play (Bunt)' },
      { id: 'intentional-walk-defense', label: 'Intentional Walk Setup' },
      { id: 'pitch-out-defense', label: 'Pitch Out / Steal Defense' },
      { id: 'pop-fly-priority', label: 'Pop Fly Priority' },
      { id: 'infield-fly', label: 'Infield Fly Situation' }
    ],
    special: [
      { id: 'extra-innings', label: 'Extra Innings' },
      { id: 'runner-on-second-start', label: 'Runner on 2B (Extra Innings)' },
      { id: 'bases-loaded', label: 'Bases Loaded Situation' },
      { id: 'scoring-position', label: 'Runner in Scoring Position' },
      { id: 'two-out-rally', label: 'Two Out Rally' },
      { id: 'double-switch', label: 'Double Switch' },
      { id: 'pinch-hit', label: 'Pinch Hit Situation' },
      { id: 'pinch-run', label: 'Pinch Runner Situation' },
      { id: 'warm-up', label: 'Warm-Up / Pre-Game' },
      { id: 'relay-drill', label: 'Relay Drill' }
    ]
  },

  // Stat categories for this sport
  statCategories: [
    // Batting
    { id: 'ab', name: 'At Bats', short: 'AB', type: 'counting', group: 'batting' },
    { id: 'r', name: 'Runs', short: 'R', type: 'counting', group: 'batting' },
    { id: 'h', name: 'Hits', short: 'H', type: 'counting', group: 'batting' },
    { id: '2b', name: 'Doubles', short: '2B', type: 'counting', group: 'batting' },
    { id: '3b', name: 'Triples', short: '3B', type: 'counting', group: 'batting' },
    { id: 'hr', name: 'Home Runs', short: 'HR', type: 'counting', group: 'batting' },
    { id: 'rbi', name: 'Runs Batted In', short: 'RBI', type: 'counting', group: 'batting' },
    { id: 'bb', name: 'Walks', short: 'BB', type: 'counting', group: 'batting' },
    { id: 'so', name: 'Strikeouts', short: 'SO', type: 'counting', group: 'batting' },
    { id: 'hbp', name: 'Hit By Pitch', short: 'HBP', type: 'counting', group: 'batting' },
    { id: 'sf', name: 'Sacrifice Flies', short: 'SF', type: 'counting', group: 'batting' },
    { id: 'sh', name: 'Sacrifice Bunts', short: 'SH', type: 'counting', group: 'batting' },
    { id: 'gidp', name: 'Grounded Into DP', short: 'GIDP', type: 'counting', group: 'batting' },
    { id: 'avg', name: 'Batting Average', short: 'AVG', type: 'calculated', group: 'batting' },
    { id: 'obp', name: 'On-Base Percentage', short: 'OBP', type: 'calculated', group: 'batting' },
    { id: 'slg', name: 'Slugging Percentage', short: 'SLG', type: 'calculated', group: 'batting' },
    { id: 'ops', name: 'OPS', short: 'OPS', type: 'calculated', group: 'batting' },
    // Baserunning
    { id: 'sb', name: 'Stolen Bases', short: 'SB', type: 'counting', group: 'baserunning' },
    { id: 'cs', name: 'Caught Stealing', short: 'CS', type: 'counting', group: 'baserunning' },
    // Pitching
    { id: 'ip', name: 'Innings Pitched', short: 'IP', type: 'counting', group: 'pitching' },
    { id: 'w', name: 'Wins', short: 'W', type: 'counting', group: 'pitching' },
    { id: 'l', name: 'Losses', short: 'L', type: 'counting', group: 'pitching' },
    { id: 'sv', name: 'Saves', short: 'SV', type: 'counting', group: 'pitching' },
    { id: 'hld', name: 'Holds', short: 'HLD', type: 'counting', group: 'pitching' },
    { id: 'era', name: 'Earned Run Average', short: 'ERA', type: 'calculated', group: 'pitching' },
    { id: 'k', name: 'Strikeouts (Pitching)', short: 'K', type: 'counting', group: 'pitching' },
    { id: 'bbp', name: 'Walks Allowed', short: 'BB', type: 'counting', group: 'pitching' },
    { id: 'ha', name: 'Hits Allowed', short: 'H', type: 'counting', group: 'pitching' },
    { id: 'er', name: 'Earned Runs', short: 'ER', type: 'counting', group: 'pitching' },
    { id: 'whip', name: 'WHIP', short: 'WHIP', type: 'calculated', group: 'pitching' },
    { id: 'k9', name: 'K/9', short: 'K/9', type: 'calculated', group: 'pitching' },
    { id: 'bb9', name: 'BB/9', short: 'BB/9', type: 'calculated', group: 'pitching' },
    { id: 'pitches', name: 'Pitch Count', short: 'PC', type: 'counting', group: 'pitching' },
    { id: 'strikes', name: 'Strikes', short: 'STR', type: 'counting', group: 'pitching' },
    { id: 'balls', name: 'Balls', short: 'B', type: 'counting', group: 'pitching' },
    // Fielding
    { id: 'po', name: 'Putouts', short: 'PO', type: 'counting', group: 'fielding' },
    { id: 'a', name: 'Assists', short: 'A', type: 'counting', group: 'fielding' },
    { id: 'e', name: 'Errors', short: 'E', type: 'counting', group: 'fielding' },
    { id: 'dp', name: 'Double Plays', short: 'DP', type: 'counting', group: 'fielding' },
    { id: 'fpct', name: 'Fielding Percentage', short: 'FPCT', type: 'calculated', group: 'fielding' },
    { id: 'pb', name: 'Passed Balls', short: 'PB', type: 'counting', group: 'fielding' },
    { id: 'wp', name: 'Wild Pitches', short: 'WP', type: 'counting', group: 'pitching' }
  ],

  // Scouting skill categories
  scoutingSkills: [
    { id: 'hitting-power', name: 'Hitting Power', icon: '💪' },
    { id: 'contact', name: 'Contact', icon: '🎯' },
    { id: 'speed', name: 'Speed', icon: '💨' },
    { id: 'arm-strength', name: 'Arm Strength', icon: '🚀' },
    { id: 'fielding', name: 'Fielding', icon: '🧤' },
    { id: 'baseball-iq', name: 'Baseball IQ', icon: '🧠' },
    { id: 'plate-discipline', name: 'Plate Discipline', icon: '👁️' }
  ],

  // Field dimensions (for SVG rendering)
  field: {
    // Standard MLB dimensions in feet
    basePaths: 90,
    youthBasePaths: 60,
    moundDistance: 60.5,
    youthMoundDistance: 46,
    moundDiameter: 18,
    moundHeight: 10, // inches
    homePlateWidth: 17, // inches
    batterBoxLength: 6,
    batterBoxWidth: 4,
    baseSize: 15, // inches (square)
    infieldArc: 95, // grass line radius from home
    outfieldFence: {
      mlb: { left: 330, leftCenter: 370, center: 400, rightCenter: 370, right: 330 },
      college: { left: 330, leftCenter: 360, center: 380, rightCenter: 360, right: 330 },
      highSchool: { left: 310, leftCenter: 340, center: 360, rightCenter: 340, right: 310 },
      littleLeague: { left: 200, center: 200, right: 200 }
    },
    foulLineAngle: 90, // degrees between foul lines
    coachBoxes: { length: 20, distance: 15 }, // feet from foul line
    onDeckCircle: 5, // feet diameter
    warningTrack: 15, // feet width
    markings: [
      'foul-lines', 'base-paths', 'batter-box', 'catcher-box', 'coach-boxes',
      'on-deck-circles', 'mound', 'rubber', 'infield-grass', 'outfield-grass',
      'warning-track', 'foul-poles', 'bases', 'home-plate', 'dugouts'
    ]
  }
};

export default baseball;
